/**
 * Company Billing Management Component
 *
 * Allows system admins to manage individual company subscriptions:
 * - View subscription details and status
 * - Create/update subscriptions
 * - Toggle NJAP member status
 * - Apply discounts
 * - Waive failed payments
 * - View payment history
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import BillingCreditsPanel from './BillingCreditsPanel';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface CompanyBillingManagementProps {
  companyId: string;
  companyName?: string;
}

interface Subscription {
  id: string;
  companyId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  status: string;
  trialEndDate: Date | null;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: Date | null;
}

interface BillingSettings {
  id: string;
  companyId: string;
  billingEmail: string | null;
  isNJAPMember: boolean;
  taxExempt: boolean;
  paymentGracePeriodEnd: Date | null;
  billingNotes: string | null;
  billedSeatOverride: number | null;
}

interface DiscountGroup {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  rateTableId?: string | null;
  rateTableName?: string | null;
}

interface PaymentHistory {
  id: string;
  stripeInvoiceId: string;
  amount: number;
  status: string;
  paidAt: Date | null;
  failedAt: Date | null;
  attemptCount: number;
  failureReason: string | null;
  createdAt: Date;
}

interface PaymentHistoryWithPI extends PaymentHistory {
  stripePaymentIntentId?: string;
  metadata?: any;
}

interface ActiveDiscount {
  couponId: string | null;
  percentOff: number | null;
  amountOff: number | null;
  duration: string | null;
  durationInMonths: number | null;
  name: string | null;
  start: string | null;
  end: string | null;
}

interface PricingBreakdown {
  licenseCount: number;
  effectiveLicenseCount: number;
  rateTableName: string;
  baseTierName: string;
  baseTierRange: string;
  baseTierAmount: number;
  hasOverflow: boolean;
  overflowCount: number;
  overflowPerSeatRate: number;
  overflowAmount: number;
  totalAmount: number;
}

interface CompanyBillingData {
  subscription: Subscription | null;
  billingSettings: BillingSettings | null;
  paymentHistory: PaymentHistoryWithPI[];
  discountGroups?: DiscountGroup[];
  company?: { id: string; name: string; licenseCount: number } | null;
  pricingBreakdown?: PricingBreakdown | null;
  billedCount?: number;
  seatUsingCount?: number;
  lastInvoiceEmailSentAt?: string | null;
}

export const CompanyBillingManagement: React.FC<CompanyBillingManagementProps> = ({
  companyId,
  companyName,
}) => {
  const { token } = useAuth();
  const [billingData, setBillingData] = useState<CompanyBillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  // Form states
  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [showApplyDiscount, setShowApplyDiscount] = useState(false);
  const [showWaivePayment, setShowWaivePayment] = useState(false);
  const [showExtendTrial, setShowExtendTrial] = useState(false);
  const [extendDays, setExtendDays] = useState<number>(14);

  // Manual billing license-count override
  const [licenseInput, setLicenseInput] = useState<string>('');
  const [licensePreview, setLicensePreview] = useState<PricingBreakdown | null>(null);
  const [licensePreviewLoading, setLicensePreviewLoading] = useState(false);
  const [savingLicense, setSavingLicense] = useState(false);

  // Standing billed-seat override (every future invoice, until cleared)
  const [seatOverrideInput, setSeatOverrideInput] = useState<string>('');
  const [savingSeatOverride, setSavingSeatOverride] = useState(false);

  const [createSubForm, setCreateSubForm] = useState({
    isNJAPMember: false,
    discountGroupIds: [] as string[],
    rateTableId: '',
    trialDays: 0,
    paymentMethodId: '',
    email: '',
    billingCycleAnchor: '',
    // Empty string = use the live seat-using (consumesLicense) count as the default.
    billedCount: '',
  });

  // Available rate tables (for the create-subscription rate-plan picker)
  const [availableRateTables, setAvailableRateTables] = useState<{ id: string; name: string; isDefault: boolean }[]>([]);

  // Inline discount + live price preview for the create-subscription modal
  const [createSubDiscount, setCreateSubDiscount] = useState({
    enabled: false,
    type: 'percent' as 'percent' | 'fixed',
    percentOff: 15,
    amountOff: 50,
    duration: 'forever' as 'once' | 'repeating' | 'forever',
    durationInMonths: 3,
    name: '',
  });
  const [createSubPreview, setCreateSubPreview] = useState<{
    licenseCount: number;
    billedCount: number;
    seatUsingCount: number;
    baseAmount: number;
    discountAmount: number;
    discountedTotal: number;
    breakdown: PricingBreakdown;
  } | null>(null);
  const [createSubPreviewLoading, setCreateSubPreviewLoading] = useState(false);

  // Discount group state
  const [allDiscountGroups, setAllDiscountGroups] = useState<DiscountGroup[]>([]);
  const [companyDiscountGroupIds, setCompanyDiscountGroupIds] = useState<string[]>([]);
  const [savingDiscountGroups, setSavingDiscountGroups] = useState(false);

  const [discountForm, setDiscountForm] = useState({
    type: 'percent' as 'percent' | 'fixed',
    percentOff: 15,
    amountOff: 50,
    duration: 'once' as 'once' | 'repeating' | 'forever',
    durationInMonths: 3,
    name: '',
  });
  const [activeDiscount, setActiveDiscount] = useState<ActiveDiscount | null>(null);
  const [showRefund, setShowRefund] = useState<string | null>(null); // payment ID to refund
  const [refundForm, setRefundForm] = useState({
    amount: 0,
    reason: 'requested_by_customer' as string,
    notes: '',
  });
  const [refundProcessing, setRefundProcessing] = useState(false);

  const [waivePaymentForm, setWaivePaymentForm] = useState({
    invoiceId: '',
  });

  const [processing, setProcessing] = useState(false);

  // Pending changes state
  const [pendingChangesData, setPendingChangesData] = useState<{
    pendingLicenseCount: number | null;
    pendingRateType: string | null;
  } | null>(null);

  useEffect(() => {
    fetchBillingData();
    fetchAllDiscountGroups();
  }, [companyId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await axios.get(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}`,
        config
      );

      setBillingData(response.data);
      // Set company's current discount group IDs from response
      if (response.data.discountGroups) {
        setCompanyDiscountGroupIds(response.data.discountGroups.map((g: DiscountGroup) => g.id));
      }
    } catch (error: any) {
      console.error('Error fetching company billing:', error);

      // If 404, it means no subscription exists yet - this is okay, not an error
      if (error.response?.status === 404) {
        setBillingData({
          subscription: null,
          billingSettings: null,
          paymentHistory: [],
        });
      } else {
        setError(error.response?.data?.message || 'Failed to load billing data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!createSubForm.email.trim()) {
      setMessage({ type: 'error', text: 'A billing email is required — invoices need somewhere real to go.' });
      return;
    }

    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/subscription`,
        {
          discountGroupIds: createSubForm.discountGroupIds.length > 0 ? createSubForm.discountGroupIds : undefined,
          rateTableId: createSubForm.rateTableId || undefined,
          isNJAPMember: createSubForm.discountGroupIds.length > 0,
          trialDays: createSubForm.trialDays,
          paymentMethodId: createSubForm.paymentMethodId || undefined,
          email: createSubForm.email.trim(),
          billingCycleAnchor: createSubForm.billingCycleAnchor || undefined,
          billedCount: createSubForm.billedCount ? parseInt(createSubForm.billedCount, 10) : undefined,
          discount: createSubDiscount.enabled
            ? {
                percentOff: createSubDiscount.type === 'percent' ? createSubDiscount.percentOff : undefined,
                amountOff: createSubDiscount.type === 'fixed' ? createSubDiscount.amountOff : undefined,
                duration: createSubDiscount.duration,
                durationInMonths:
                  createSubDiscount.duration === 'repeating' ? createSubDiscount.durationInMonths : undefined,
                name: createSubDiscount.name || undefined,
              }
            : undefined,
        },
        config
      );

      if (response.data.discountWarning) {
        setMessage({ type: 'warning', text: response.data.discountWarning });
      } else {
        setMessage({
          type: 'success',
          text: `Subscription created successfully! Billing ${response.data.billedCount} seat(s) (${response.data.seatUsingCount} seat-using).`,
        });
      }

      setShowCreateSubscription(false);
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.response?.data?.error || 'Failed to create subscription',
      });
    } finally {
      setProcessing(false);
    }
  };

  // Live preview of the exact monthly price for the create-subscription modal,
  // reflecting the selected rate table and any inline discount (debounced).
  useEffect(() => {
    if (!showCreateSubscription) {
      setCreateSubPreview(null);
      return;
    }
    let cancelled = false;
    setCreateSubPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (createSubForm.rateTableId) params.set('rateTableId', createSubForm.rateTableId);
        if (createSubForm.billedCount) params.set('billedCount', createSubForm.billedCount);
        if (createSubDiscount.enabled) {
          if (createSubDiscount.type === 'percent') {
            params.set('percentOff', String(createSubDiscount.percentOff));
          } else {
            params.set('amountOff', String(createSubDiscount.amountOff));
          }
        }
        const response = await axios.get(
          `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/subscription/preview?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setCreateSubPreview(response.data);
      } catch {
        if (!cancelled) setCreateSubPreview(null);
      } finally {
        if (!cancelled) setCreateSubPreviewLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    showCreateSubscription,
    createSubForm.rateTableId,
    createSubForm.billedCount,
    createSubDiscount.enabled,
    createSubDiscount.type,
    createSubDiscount.percentOff,
    createSubDiscount.amountOff,
    companyId,
    token,
  ]);

  // Live preview of the monthly price for the typed license count (debounced).
  useEffect(() => {
    const n = parseInt(licenseInput, 10);
    if (!Number.isInteger(n) || n < 1 || n > 100) {
      setLicensePreview(null);
      return;
    }
    let cancelled = false;
    setLicensePreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/billing-license/preview?count=${n}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setLicensePreview(response.data.breakdown);
      } catch {
        if (!cancelled) setLicensePreview(null);
      } finally {
        if (!cancelled) setLicensePreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [licenseInput, companyId, token]);

  const handleReissueInvoice = async () => {
    const n = parseInt(licenseInput, 10);
    if (!Number.isInteger(n) || n < 1 || n > 100) {
      setMessage({ type: 'error', text: 'Billed seat count must be a whole number between 1 and 100' });
      return;
    }
    const priceText = licensePreview ? ` at $${(licensePreview.totalAmount / 100).toFixed(2)}` : '';
    const actual = billingData?.company?.licenseCount;
    if (!window.confirm(
      `Reissue the current invoice billed for ${n} seat(s)${priceText}?\n\n` +
      `This is a ONE-TIME adjustment to the current invoice: the open invoice is voided and reissued at this amount. ` +
      `It does NOT change the company's provisioned seats${actual != null ? ` (${actual})` : ''} or usage, and the next invoice will bill their actual seat count.`
    )) {
      return;
    }
    try {
      setSavingLicense(true);
      setMessage(null);
      const response = await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/reissue-invoice`,
        { billedCount: n },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: response.data.message || 'Invoice reissued' });
      setLicenseInput('');
      setLicensePreview(null);
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error reissuing invoice:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to reissue invoice',
      });
    } finally {
      setSavingLicense(false);
    }
  };

  const handleSetSeatOverride = async () => {
    const n = parseInt(seatOverrideInput, 10);
    if (!Number.isInteger(n) || n < 1 || n > 100) {
      setMessage({ type: 'error', text: 'Billed seat override must be a whole number between 1 and 100' });
      return;
    }
    try {
      setSavingSeatOverride(true);
      setMessage(null);
      const response = await axios.put(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/billed-seat-override`,
        { billedSeatOverride: n },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: response.data.message || 'Billed seat override set' });
      setSeatOverrideInput('');
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error setting seat override:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to set seat override' });
    } finally {
      setSavingSeatOverride(false);
    }
  };

  const handleClearSeatOverride = async () => {
    try {
      setSavingSeatOverride(true);
      setMessage(null);
      const response = await axios.delete(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/billed-seat-override`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: response.data.message || 'Billed seat override cleared' });
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error clearing seat override:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to clear seat override' });
    } finally {
      setSavingSeatOverride(false);
    }
  };

  const [sendingInvoice, setSendingInvoice] = useState(false);
  const handleSendInvoice = async () => {
    try {
      setSendingInvoice(true);
      setMessage(null);
      const response = await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/send-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: response.data.emailSent ? 'success' : 'warning', text: response.data.message });
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error sending invoice:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to send invoice' });
    } finally {
      setSendingInvoice(false);
    }
  };

  const fetchPendingChanges = async () => {
    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await axios.get(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}`,
        config
      );

      setPendingChangesData(response.data.pendingChanges || null);
    } catch (error: any) {
      // Pending changes are optional; fail silently
    }
  };

  const fetchDiscount = async () => {
    if (!billingData?.subscription) return;
    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      const response = await axios.get(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/discount`,
        config
      );
      setActiveDiscount(response.data.discount);
    } catch (error: any) {
      console.error('Error fetching discount:', error);
    }
  };

  useEffect(() => {
    if (billingData?.subscription) {
      fetchDiscount();
      fetchPendingChanges();
    }
  }, [billingData?.subscription]);

  const fetchAllDiscountGroups = async () => {
    try {
      const config = {
        headers: { 'Authorization': `Bearer ${token}` },
      };
      const [groupsRes, rateTablesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/system-admin/billing/discount-groups`, config),
        axios.get(`${API_BASE_URL}/api/system-admin/billing/rate-tables`, config),
      ]);
      setAllDiscountGroups(groupsRes.data.groups || []);
      setAvailableRateTables(
        (rateTablesRes.data.rateTables || []).map((rt: any) => ({
          id: rt.id,
          name: rt.name,
          isDefault: rt.isDefault,
        }))
      );
    } catch (error) {
      console.error('Error fetching discount groups / rate tables:', error);
    }
  };

  const handleSaveDiscountGroups = async (newGroupIds: string[]) => {
    try {
      setSavingDiscountGroups(true);
      setMessage(null);

      const config = {
        headers: { 'Authorization': `Bearer ${token}` },
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/discount-groups`,
        { groupIds: newGroupIds },
        config
      );

      setCompanyDiscountGroupIds(newGroupIds);

      setMessage({
        type: 'success',
        text: response.data.message || 'Discount groups updated',
      });

      await fetchBillingData();
    } catch (error: any) {
      console.error('Error updating discount groups:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update discount groups',
      });
    } finally {
      setSavingDiscountGroups(false);
    }
  };

  const toggleDiscountGroup = (groupId: string) => {
    const newIds = companyDiscountGroupIds.includes(groupId)
      ? companyDiscountGroupIds.filter((id) => id !== groupId)
      : [...companyDiscountGroupIds, groupId];
    handleSaveDiscountGroups(newIds);
  };

  const handleExtendTrial = async () => {
    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/extend-trial`,
        { days: extendDays },
        config
      );

      setMessage({
        type: 'success',
        text: `Trial extended by ${extendDays} days successfully!`,
      });

      setShowExtendTrial(false);
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error extending trial:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to extend trial',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleEndTrialNow = async () => {
    if (!window.confirm('Are you sure you want to end the trial and start billing immediately? This cannot be undone.')) {
      return;
    }

    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/end-trial`,
        {},
        config
      );

      setMessage({
        type: 'success',
        text: 'Trial ended successfully. Billing has started.',
      });

      await fetchBillingData();
    } catch (error: any) {
      console.error('Error ending trial:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to end trial',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async (immediate: boolean) => {
    const confirmMessage = immediate
      ? 'Are you sure you want to cancel this subscription IMMEDIATELY? The company will lose access right away.'
      : 'Are you sure you want to cancel this subscription at the end of the billing period?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/cancel-subscription`,
        { immediate },
        config
      );

      setMessage({
        type: 'success',
        text: immediate
          ? 'Subscription canceled immediately.'
          : 'Subscription will be canceled at the end of the billing period.',
      });

      await fetchBillingData();
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to cancel subscription',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!window.confirm('Are you sure you want to reactivate this subscription?')) {
      return;
    }

    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/reactivate-subscription`,
        {},
        config
      );

      setMessage({
        type: 'success',
        text: 'Subscription reactivated successfully.',
      });

      await fetchBillingData();
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to reactivate subscription',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleApplyDiscount = async () => {
    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const body: any = {
        duration: discountForm.duration,
        name: discountForm.name || undefined,
      };

      if (discountForm.type === 'percent') {
        body.percentOff = discountForm.percentOff;
      } else {
        body.amountOff = discountForm.amountOff;
      }

      if (discountForm.duration === 'repeating') {
        body.durationInMonths = discountForm.durationInMonths;
      }

      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/discount`,
        body,
        config
      );

      setMessage({
        type: 'success',
        text: 'Discount applied successfully!',
      });

      setShowApplyDiscount(false);
      await fetchDiscount();
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error applying discount:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to apply discount',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!window.confirm('Remove the active discount from this subscription?')) return;
    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.delete(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/discount`,
        config
      );

      setMessage({
        type: 'success',
        text: 'Discount removed successfully!',
      });

      setActiveDiscount(null);
    } catch (error: any) {
      console.error('Error removing discount:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to remove discount',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRefund = async (paymentIntentId: string) => {
    if (!window.confirm(`Process a refund of $${refundForm.amount.toFixed(2)}?`)) return;
    try {
      setRefundProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/refund`,
        {
          paymentIntentId,
          amount: refundForm.amount > 0 ? refundForm.amount : undefined,
          reason: refundForm.reason,
          notes: refundForm.notes || undefined,
        },
        config
      );

      setMessage({
        type: 'success',
        text: 'Refund processed successfully!',
      });

      setShowRefund(null);
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error processing refund:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to process refund',
      });
    } finally {
      setRefundProcessing(false);
    }
  };

  const handleWaivePayment = async () => {
    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/waive-payment`,
        { invoiceId: waivePaymentForm.invoiceId },
        config
      );

      setMessage({
        type: 'success',
        text: 'Payment waived successfully!',
      });

      setShowWaivePayment(false);
      setWaivePaymentForm({ invoiceId: '' });
      await fetchBillingData();
    } catch (error: any) {
      console.error('Error waiving payment:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to waive payment',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleResetBilling = async () => {
    if (!window.confirm(
      `Are you sure you want to RESET ALL BILLING DATA for this company?\n\n` +
      `This will:\n` +
      `- Cancel all Stripe subscriptions\n` +
      `- Delete the Stripe customer\n` +
      `- Clear all payment history, billing snapshots, and events\n` +
      `- Reset subscription status\n\n` +
      `The company will need to subscribe again from scratch.`
    )) {
      return;
    }

    try {
      setProcessing(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/reset-billing`,
        {},
        config
      );

      setMessage({
        type: 'success',
        text: `Billing reset complete. ${response.data.results?.length || 0} operations performed.`,
      });

      await fetchBillingData();
    } catch (error: any) {
      console.error('Error resetting billing:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to reset billing',
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | string | null): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600 dark:text-gray-400">Loading billing data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-6">
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
          Error Loading Billing Data
        </h3>
        <p className="text-red-700 dark:text-red-300">{error}</p>
      </div>
    );
  }

  const { subscription, billingSettings, paymentHistory } = billingData || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Billing Management
        </h2>
        {companyName && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{companyName}</p>
        )}
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
              : message.type === 'warning'
              ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Subscription Status Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Subscription Status
          </h3>
          {subscription && (
            <span
              className={`px-3 py-1 text-sm font-semibold rounded-full ${
                subscription.status === 'active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  : subscription.status === 'trialing'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                  : subscription.status === 'past_due'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}
            >
              {subscription.status}
            </span>
          )}
        </div>

        {subscription ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Subscription ID
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                {subscription.stripeSubscriptionId || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Customer ID
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                {subscription.stripeCustomerId || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Current Period
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {formatDate(subscription.currentPeriodStart)} -{' '}
                {formatDate(subscription.currentPeriodEnd)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Trial End Date
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {formatDate(subscription.trialEndDate)}
              </p>
              {subscription.status === 'trialing' && subscription.trialEndDate && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setShowExtendTrial(true)}
                    disabled={processing}
                    className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Extend Trial
                  </button>
                  <button
                    onClick={handleEndTrialNow}
                    disabled={processing}
                    className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Convert to Paid
                  </button>
                </div>
              )}
            </div>
            {subscription.cancelAtPeriodEnd && (
              <div className="col-span-2">
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Subscription will cancel at period end ({formatDate(subscription.currentPeriodEnd)})
                  </p>
                  <button
                    onClick={handleReactivateSubscription}
                    disabled={processing}
                    className="mt-2 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reactivate Subscription
                  </button>
                </div>
              </div>
            )}

            {/* Subscription Management Actions */}
            {subscription.status !== 'canceled' && !subscription.cancelAtPeriodEnd && (
              <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Subscription Management
                </h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleCancelSubscription(false)}
                    disabled={processing}
                    className="px-3 py-1.5 text-sm bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel at Period End
                  </button>
                  <button
                    onClick={() => handleCancelSubscription(true)}
                    disabled={processing}
                    className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel Immediately
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <strong>Cancel at Period End:</strong> Company keeps access until {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'period ends'}.<br />
                  <strong>Cancel Immediately:</strong> Company loses access right away.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No active subscription found
            </p>
            <button
              onClick={() => setShowCreateSubscription(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              Create Subscription
            </button>
          </div>
        )}
      </div>

      {/* Adjust Current Invoice Card */}
      {subscription && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Adjust Current Invoice
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            One-time override of the seat count billed on the <strong>current</strong> invoice. Voids the open invoice and reissues it at the new amount. Does not change provisioned seats or usage; the next invoice bills their actual seat count.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current / actual */}
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/40">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Provisioned seats (unchanged)
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                {billingData?.company?.licenseCount ?? '—'}{' '}
                <span className="text-base font-normal text-gray-500 dark:text-gray-400">seats</span>
              </p>
              {billingData?.pricingBreakdown && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Recurring: ${(billingData.pricingBreakdown.totalAmount / 100).toFixed(2)}/mo · {billingData.pricingBreakdown.rateTableName} · {billingData.pricingBreakdown.baseTierName}
                  {billingData.pricingBreakdown.hasOverflow && ` + ${billingData.pricingBreakdown.overflowCount} seat overflow`}
                </p>
              )}
            </div>

            {/* Override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bill this invoice for (seats, 1–100)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={licenseInput}
                onChange={(e) => setLicenseInput(e.target.value)}
                placeholder={billingData?.company?.licenseCount?.toString() || 'e.g., 10'}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-2 min-h-[1.25rem] text-sm">
                {licensePreviewLoading && <span className="text-gray-400 animate-pulse">Calculating…</span>}
                {!licensePreviewLoading && licensePreview && (
                  <span className="text-gray-700 dark:text-gray-300">
                    This invoice:{' '}
                    <strong className="text-gray-900 dark:text-white">
                      ${(licensePreview.totalAmount / 100).toFixed(2)}
                    </strong>
                    {' · '}{licensePreview.rateTableName} {licensePreview.baseTierName}
                    {licensePreview.hasOverflow
                      ? ` (+${licensePreview.overflowCount} seat${licensePreview.overflowCount !== 1 ? 's' : ''} @ $${(licensePreview.overflowPerSeatRate / 100).toFixed(2)})`
                      : licensePreview.effectiveLicenseCount !== parseInt(licenseInput, 10)
                        ? ` (flat rate covers up to ${licensePreview.effectiveLicenseCount} seats)`
                        : ''}
                  </span>
                )}
              </div>
              <button
                onClick={handleReissueInvoice}
                disabled={savingLicense || !licensePreview}
                className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingLicense ? 'Reissuing…' : 'Void & Reissue Current Invoice'}
              </button>
            </div>
          </div>

          {/* Standing billed-seat override — every future invoice, not just the current one */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Standing Billed-Seat Override</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Unlike the one-time reissue above, this applies to every future invoice until cleared. Never touches provisioned seats.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Billing <strong>{billingData?.billedCount ?? '—'}</strong> seat(s)
                {billingSettings?.billedSeatOverride ? (
                  <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">override active</span>
                ) : null}
                {' — '}{billingData?.seatUsingCount ?? '—'} seat-using of {billingData?.company?.licenseCount ?? '—'} provisioned
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={seatOverrideInput}
                  onChange={(e) => setSeatOverrideInput(e.target.value)}
                  placeholder="e.g., 10"
                  className="w-24 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <button
                  onClick={handleSetSeatOverride}
                  disabled={savingSeatOverride || !seatOverrideInput}
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  Set
                </button>
                {billingSettings?.billedSeatOverride ? (
                  <button
                    onClick={handleClearSeatOverride}
                    disabled={savingSeatOverride}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Clear override
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Settings Card */}
      {billingSettings && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Billing Settings
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Rate Type & Discount Groups
              </p>
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`px-3 py-1 text-sm font-semibold rounded-full ${
                    companyDiscountGroupIds.length > 0
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                  }`}
                >
                  {companyDiscountGroupIds.length > 0 ? 'Discounted' : 'Standard'}
                </span>
                {companyDiscountGroupIds.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {allDiscountGroups
                      .filter((g) => companyDiscountGroupIds.includes(g.id))
                      .map((g) => (
                        <span
                          key={g.id}
                          className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                        >
                          {g.name}
                        </span>
                      ))}
                  </div>
                )}
              </div>

              {/* Discount Group Checkboxes */}
              {allDiscountGroups.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {allDiscountGroups.map((group) => (
                    <label key={group.id} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={companyDiscountGroupIds.includes(group.id)}
                        onChange={() => toggleDiscountGroup(group.id)}
                        disabled={savingDiscountGroups}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {group.name}
                      </span>
                      {group.description && (
                        <span className="text-xs text-gray-400" title={group.description}>
                          (?)
                        </span>
                      )}
                    </label>
                  ))}
                  {savingDiscountGroups && (
                    <span className="text-xs text-gray-400 animate-pulse">Saving...</span>
                  )}
                </div>
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Companies with any discount group get discounted rates. Rate changes take effect on the next billing cycle.
              </p>

              {/* Pending Changes Indicator */}
              {pendingChangesData && (pendingChangesData.pendingLicenseCount || pendingChangesData.pendingRateType) && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1">
                    Pending Changes (effective next billing cycle)
                  </p>
                  <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
                    {pendingChangesData.pendingLicenseCount && (
                      <li>License count: {pendingChangesData.pendingLicenseCount}</li>
                    )}
                    {pendingChangesData.pendingRateType && (
                      <li>Rate type: {pendingChangesData.pendingRateType === 'discounted' ? 'Discounted' : 'Standard'}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Billing Email
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {billingSettings.billingEmail || 'Using company admin email'}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Tax Exempt
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {billingSettings.taxExempt ? 'Yes' : 'No'}
              </p>
            </div>

            {billingSettings.paymentGracePeriodEnd && (
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Grace Period End
                </p>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-semibold">
                  {formatDate(billingSettings.paymentGracePeriodEnd)}
                </p>
              </div>
            )}

            {billingSettings.billingNotes && (
              <div className="col-span-2">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Billing Notes
                </p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {billingSettings.billingNotes}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active Discount */}
      {activeDiscount && (
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg shadow-lg p-6 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-200">
              Active Discount
            </h3>
            <button
              onClick={handleRemoveDiscount}
              disabled={processing}
              className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              Remove Discount
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Discount</p>
              <p className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                {activeDiscount.percentOff
                  ? `${activeDiscount.percentOff}% off`
                  : activeDiscount.amountOff
                  ? `$${activeDiscount.amountOff.toFixed(2)} off`
                  : 'Unknown'}
              </p>
            </div>
            <div>
              <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Duration</p>
              <p className="text-sm text-purple-900 dark:text-purple-100">
                {activeDiscount.duration === 'once' ? 'One-time'
                  : activeDiscount.duration === 'forever' ? 'Forever'
                  : activeDiscount.durationInMonths ? `${activeDiscount.durationInMonths} months` : activeDiscount.duration}
              </p>
            </div>
            {activeDiscount.name && (
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Name</p>
                <p className="text-sm text-purple-900 dark:text-purple-100">{activeDiscount.name}</p>
              </div>
            )}
            {activeDiscount.end && (
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Ends</p>
                <p className="text-sm text-purple-900 dark:text-purple-100">{formatDate(activeDiscount.end)}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={handleSendInvoice}
          disabled={!subscription || processing || sendingInvoice}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sendingInvoice ? 'Sending…' : 'Send Invoice'}
        </button>
        {billingData?.lastInvoiceEmailSentAt && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Last sent {formatDate(billingData.lastInvoiceEmailSentAt)}
          </span>
        )}
        <button
          onClick={() => setShowApplyDiscount(true)}
          disabled={!subscription || processing}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Apply Discount
        </button>
        <button
          onClick={() => setShowWaivePayment(true)}
          disabled={!subscription || processing}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Waive Payment
        </button>
        <button
          onClick={handleResetBilling}
          disabled={processing}
          className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset Billing
        </button>
      </div>

      {/* Create Subscription Modal */}
      {showCreateSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Create Subscription
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Billing Email
                </label>
                <input
                  type="email"
                  placeholder="billing@company.com"
                  value={createSubForm.email}
                  onChange={(e) =>
                    setCreateSubForm({ ...createSubForm, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Used for Stripe invoices and billing notifications — required.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Billed Seats <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional override)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  placeholder={createSubPreview ? `${createSubPreview.seatUsingCount} seat-using (default)` : 'Default: seat-using count'}
                  value={createSubForm.billedCount}
                  onChange={(e) => setCreateSubForm({ ...createSubForm, billedCount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Defaults to the company's seat-using (consumesLicense) count, not the provisioned license count. Leave blank unless you need to bill a different number.
                </p>
              </div>

              {availableRateTables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rate Table
                  </label>
                  <select
                    value={createSubForm.rateTableId}
                    onChange={(e) =>
                      setCreateSubForm({ ...createSubForm, rateTableId: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Use default rate table</option>
                    {availableRateTables.map((rt) => (
                      <option key={rt.id} value={rt.id}>
                        {rt.name}{rt.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Pick a rate table directly. A discount group will be linked automatically.
                  </p>
                </div>
              )}

              {allDiscountGroups.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Discount Groups <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {allDiscountGroups.map((group) => (
                      <label key={group.id} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={createSubForm.discountGroupIds.includes(group.id)}
                          onChange={(e) => {
                            const newIds = e.target.checked
                              ? [...createSubForm.discountGroupIds, group.id]
                              : createSubForm.discountGroupIds.filter((id) => id !== group.id);
                            setCreateSubForm({ ...createSubForm, discountGroupIds: newIds });
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {group.name}
                          {group.rateTableName && (
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                              · {group.rateTableName}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Or assign existing discount groups directly.
                  </p>
                </div>
              )}

              {/* Inline discount — applied at creation so the billing email reflects it */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createSubDiscount.enabled}
                    onChange={(e) =>
                      setCreateSubDiscount({ ...createSubDiscount, enabled: e.target.checked })
                    }
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Apply a discount
                  </span>
                </label>

                {createSubDiscount.enabled && (
                  <div className="mt-3 space-y-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setCreateSubDiscount({ ...createSubDiscount, type: 'percent' })}
                        className={`flex-1 px-3 py-1.5 text-sm rounded-lg border ${
                          createSubDiscount.type === 'percent'
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Percentage Off
                      </button>
                      <button
                        type="button"
                        onClick={() => setCreateSubDiscount({ ...createSubDiscount, type: 'fixed' })}
                        className={`flex-1 px-3 py-1.5 text-sm rounded-lg border ${
                          createSubDiscount.type === 'fixed'
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Fixed Amount Off
                      </button>
                    </div>

                    {createSubDiscount.type === 'percent' ? (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Percentage Off (%)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={createSubDiscount.percentOff}
                          onChange={(e) =>
                            setCreateSubDiscount({
                              ...createSubDiscount,
                              percentOff: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Amount Off ($)
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={createSubDiscount.amountOff}
                          onChange={(e) =>
                            setCreateSubDiscount({
                              ...createSubDiscount,
                              amountOff: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Duration
                      </label>
                      <select
                        value={createSubDiscount.duration}
                        onChange={(e) =>
                          setCreateSubDiscount({
                            ...createSubDiscount,
                            duration: e.target.value as 'once' | 'repeating' | 'forever',
                          })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="forever">Forever (every invoice)</option>
                        <option value="once">Once (first invoice only)</option>
                        <option value="repeating">Repeating (X months)</option>
                      </select>
                    </div>

                    {createSubDiscount.duration === 'repeating' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Duration (months)
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={createSubDiscount.durationInMonths}
                          onChange={(e) =>
                            setCreateSubDiscount({
                              ...createSubDiscount,
                              durationInMonths: parseInt(e.target.value) || 1,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Label (optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. NJAP loyalty discount"
                        value={createSubDiscount.name}
                        onChange={(e) =>
                          setCreateSubDiscount({ ...createSubDiscount, name: e.target.value })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trial Period (days)
                </label>
                <input
                  type="number"
                  min="0"
                  max="90"
                  value={createSubForm.trialDays}
                  onChange={(e) =>
                    setCreateSubForm({ ...createSubForm, trialDays: Math.max(0, parseInt(e.target.value, 10) || 0) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Method ID (optional)
                </label>
                <input
                  type="text"
                  placeholder="pm_..."
                  value={createSubForm.paymentMethodId}
                  onChange={(e) =>
                    setCreateSubForm({ ...createSubForm, paymentMethodId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Billing Cycle Start Date (optional)
                </label>
                <input
                  type="date"
                  min={new Date(Date.now() + 86400000).toLocaleDateString('en-CA')}
                  value={createSubForm.billingCycleAnchor}
                  onChange={(e) =>
                    setCreateSubForm({ ...createSubForm, billingCycleAnchor: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Sets the monthly billing date. If empty, the first invoice is issued today and renews on today&apos;s date each month. If set, nothing is billed until that date, then it renews monthly on it.
                </p>
              </div>
            </div>

            {/* Live price preview — exact amount the company will be billed */}
            <div className="mt-5 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 p-4">
              {createSubPreviewLoading && !createSubPreview ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Calculating price…</p>
              ) : createSubPreview ? (
                <div className="space-y-1.5 text-sm">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Billing {createSubPreview.billedCount} seat{createSubPreview.billedCount !== 1 ? 's' : ''}
                    {createSubForm.billedCount ? ' (override)' : ''} — {createSubPreview.seatUsingCount} seat-using of {createSubPreview.licenseCount} provisioned
                  </div>
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>
                      {createSubPreview.breakdown.rateTableName} · {createSubPreview.billedCount} seat
                      {createSubPreview.billedCount !== 1 ? 's' : ''} ({createSubPreview.breakdown.baseTierRange})
                    </span>
                    <span>${(createSubPreview.baseAmount / 100).toFixed(2)}/mo</span>
                  </div>
                  {createSubPreview.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600 dark:text-green-400">
                      <span>
                        Discount
                        {createSubDiscount.type === 'percent'
                          ? ` (${createSubDiscount.percentOff}% off)`
                          : ` ($${createSubDiscount.amountOff.toFixed(2)} off)`}
                      </span>
                      <span>-${(createSubPreview.discountAmount / 100).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700 text-base font-semibold text-gray-900 dark:text-white">
                    <span>Monthly total</span>
                    <span>${(createSubPreview.discountedTotal / 100).toFixed(2)}/mo</span>
                  </div>
                  {createSubDiscount.enabled && createSubDiscount.duration !== 'forever' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                      Discount applies{' '}
                      {createSubDiscount.duration === 'once'
                        ? 'to the first invoice only'
                        : `for ${createSubDiscount.durationInMonths} month${createSubDiscount.durationInMonths !== 1 ? 's' : ''}`}
                      ; renews at ${(createSubPreview.baseAmount / 100).toFixed(2)}/mo after.
                    </p>
                  )}
                  {createSubForm.trialDays > 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                      First charge after the {createSubForm.trialDays}-day trial.
                    </p>
                  ) : createSubForm.billingCycleAnchor ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                      First invoice on {createSubForm.billingCycleAnchor}.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                      First invoice is issued today.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Price unavailable — check the rate table selection.
                </p>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleCreateSubscription}
                disabled={processing || !createSubForm.email.trim()}
                title={!createSubForm.email.trim() ? 'A billing email is required' : undefined}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {processing ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => setShowCreateSubscription(false)}
                disabled={processing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Trial Modal */}
      {showExtendTrial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Extend Trial Period
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Additional Days
                </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Common values: 7 days, 14 days, 30 days
                </p>
              </div>

              {billingData?.subscription?.trialEndDate && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Current trial end: {formatDate(billingData.subscription.trialEndDate)}
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                    New trial end: {formatDate(new Date(new Date(billingData.subscription.trialEndDate).getTime() + extendDays * 24 * 60 * 60 * 1000))}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleExtendTrial}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {processing ? 'Extending...' : 'Extend Trial'}
              </button>
              <button
                onClick={() => setShowExtendTrial(false)}
                disabled={processing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Discount Modal */}
      {showApplyDiscount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Create Discount
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Discount Type
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDiscountForm({ ...discountForm, type: 'percent' })}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                      discountForm.type === 'percent'
                        ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Percentage Off
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountForm({ ...discountForm, type: 'fixed' })}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                      discountForm.type === 'fixed'
                        ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Fixed Amount Off
                  </button>
                </div>
              </div>

              {discountForm.type === 'percent' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Percentage Off (%)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={discountForm.percentOff}
                    onChange={(e) => setDiscountForm({ ...discountForm, percentOff: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount Off ($)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={discountForm.amountOff}
                    onChange={(e) => setDiscountForm({ ...discountForm, amountOff: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration
                </label>
                <select
                  value={discountForm.duration}
                  onChange={(e) => setDiscountForm({ ...discountForm, duration: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="once">One-time</option>
                  <option value="repeating">Repeating (X months)</option>
                  <option value="forever">Forever</option>
                </select>
              </div>

              {discountForm.duration === 'repeating' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration (months)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={discountForm.durationInMonths}
                    onChange={(e) => setDiscountForm({ ...discountForm, durationInMonths: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., NJAP loyalty discount"
                  value={discountForm.name}
                  onChange={(e) => setDiscountForm({ ...discountForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleApplyDiscount}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {processing ? 'Applying...' : 'Create & Apply Discount'}
              </button>
              <button
                onClick={() => setShowApplyDiscount(false)}
                disabled={processing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waive Payment Modal */}
      {showWaivePayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Waive Payment
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Stripe Invoice ID
              </label>
              <input
                type="text"
                placeholder="in_..."
                value={waivePaymentForm.invoiceId}
                onChange={(e) => setWaivePaymentForm({ invoiceId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                This will clear the grace period and mark the payment as waived.
              </p>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleWaivePayment}
                disabled={processing || !waivePaymentForm.invoiceId}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {processing ? 'Waiving...' : 'Waive'}
              </button>
              <button
                onClick={() => {
                  setShowWaivePayment(false);
                  setWaivePaymentForm({ invoiceId: '' });
                }}
                disabled={processing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment History */}
      {paymentHistory && paymentHistory.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Payment History
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Invoice ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paymentHistory.map((payment: PaymentHistoryWithPI) => (
                  <React.Fragment key={payment.id}>
                    <tr className={payment.status === 'refunded' ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                        {payment.stripeInvoiceId.substring(0, 20)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            payment.status === 'succeeded'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : payment.status === 'refunded'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}
                        >
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(payment.paidAt || payment.failedAt || payment.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {payment.status === 'succeeded' && payment.stripePaymentIntentId && (
                          <button
                            onClick={() => {
                              setShowRefund(payment.id);
                              setRefundForm({
                                amount: Number(payment.amount),
                                reason: 'requested_by_customer',
                                notes: '',
                              });
                            }}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium text-xs"
                          >
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                    {showRefund === payment.id && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-blue-50 dark:bg-blue-900/20">
                          <div className="flex flex-wrap items-end gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Amount ($)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                max={Number(payment.amount)}
                                value={refundForm.amount}
                                onChange={(e) => setRefundForm({ ...refundForm, amount: parseFloat(e.target.value) || 0 })}
                                className="w-28 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Reason</label>
                              <select
                                value={refundForm.reason}
                                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                                className="w-48 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="requested_by_customer">Customer Request</option>
                                <option value="duplicate">Duplicate</option>
                                <option value="fraudulent">Fraudulent</option>
                              </select>
                            </div>
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
                              <input
                                type="text"
                                placeholder="Optional notes..."
                                value={refundForm.notes}
                                onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                            <button
                              onClick={() => handleRefund(payment.stripePaymentIntentId!)}
                              disabled={refundProcessing}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded transition-colors disabled:opacity-50"
                            >
                              {refundProcessing ? 'Processing...' : 'Process Refund'}
                            </button>
                            <button
                              onClick={() => setShowRefund(null)}
                              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase 5: Billing Credits */}
      <BillingCreditsPanel companyId={companyId} selectedDiscountGroupIds={companyDiscountGroupIds} />
    </div>
  );
};
